import { Popup } from 'react-leaflet';
import { useContext, useState } from 'react';
import { MapContext, ViewOnlyContext } from '../context';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Divider, Stack, Typography, Checkbox, IconButton } from '@mui/material';
import { MapPin, StationType, Todo } from '../schema';
import StationNotes from './StationNotes';
import CleanUpButton from './CleanUpButton';
import DeleteTodoDialog from './DeleteTodoDialog';
import { db } from '../instant';

interface Props {
    pin: MapPin;
    station: StationType;
    iconSrc: string;
    todo?: Todo;
    onDelete: () => void;
}

export default function StationMarkerPopup(props: Props) {
    const { editMode } = useContext(MapContext);
    const viewOnly = useContext(ViewOnlyContext);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const handleToggleTodoComplete = async (ev: React.MouseEvent) => {
        ev.stopPropagation();
        if (props.todo) {
            await db.transact(db.tx.todos[props.todo.id].update({ completed: !props.todo.completed }));
        }
    };

    const handleDeleteTodo = () => {
        setDeleteDialogOpen(true);
    };

    // todo
    if (props.todo) {
        return (
            <Popup>
                <Stack sx={{ alignItems: 'center' }} direction="column" spacing={1}>
                    <Stack sx={{ alignItems: 'center' }} direction="row">
                        <Typography fontWeight="bold" style={{ margin: '5px', marginRight: '10px' }}>
                            {props.station.name as string}
                        </Typography>
                    </Stack>
                    <Stack direction="row" sx={{ alignItems: 'center' }}>
                        <img style={{ marginRight: '10px' }} alt="icon" width="40px" height="40px" src={props.iconSrc} />
                        <Typography sx={{ margin: '2px' }}>{props.station.description as string}</Typography>
                    </Stack>
                    <Divider flexItem />
                    <Stack sx={{ alignItems: 'center', width: '100%' }} direction="row" spacing={1}>
                        <Checkbox
                            checked={props.todo.completed}
                            onClick={handleToggleTodoComplete}
                            disabled={viewOnly}
                            sx={{ padding: 0 }}
                        />
                        <Typography fontWeight="bold" sx={{ flex: 1 }}>
                            TODO: {props.todo.todoType?.name || 'Task'}
                        </Typography>
                        {!viewOnly && (
                            <IconButton size="small" color="error" onClick={handleDeleteTodo}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Stack>
                    {props.todo.notes ? (
                        <Typography variant="body2" color="text.secondary" sx={{ width: '100%', textAlign: 'left' }}>
                            {props.todo.notes}
                        </Typography>
                    ) : null}
                </Stack>
                <DeleteTodoDialog todo={props.todo} open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} />
            </Popup>
        );
    }

    // regular
    return (
        <Popup>
            <Stack sx={{ alignItems: 'center' }} direction="column" spacing={1}>
                <Stack sx={{ alignItems: 'center' }} direction="row">
                    {editMode && (
                        <Button
                            onClick={ev => {
                                // Delete the marker
                                props.onDelete();
                                // Prevent a click event on the map
                                // from accidentally creating a new marker
                                ev.stopPropagation();
                            }}
                            sx={{ width: '40px', minWidth: '40px', marginRight: 1 }}
                        >
                            <DeleteIcon />
                        </Button>
                    )}
                    <Typography fontWeight="bold" style={{ margin: '5px', marginRight: '10px' }}>
                        {props.station.name}
                    </Typography>
                    {editMode && <Box sx={{ width: '40px' }} />}
                </Stack>
                <Stack direction="row" sx={{ alignItems: 'center' }}>
                    <img style={{ marginRight: '10px' }} alt="icon" width="40px" height="40px" src={props.iconSrc} />
                    <Typography sx={{ margin: '2px' }}>{props.station.description}</Typography>
                </Stack>
                <CleanUpButton pin={props.pin} />
                <Divider flexItem />
                <StationNotes pinId={props.pin.id} notes={props.pin.notes} />
            </Stack>
        </Popup>
    );
}
